import WaSaleButton from "@/components/whatsapp/WaSaleButton";
import WaReadyButton from "@/components/whatsapp/WaReadyButton";
import CustomerPicker from "@/components/customers/CustomerPicker";
import { useState, useEffect, useCallback, useMemo } from "react";
import { api, downloadAuthed } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { servicesTotalEur } from "@/lib/serviceTotals";
import ServiceLinesEditor, { serviceLinesPayload } from "@/components/admin/ServiceLinesEditor";
import ConfirmDialog from "@/components/ConfirmDialog";
import ReceiptPrinter from "@/components/admin/ReceiptPrinter";
import LabelEditor from "@/components/admin/LabelEditor";
import FailureReasonsPicker from "@/components/admin/FailureReasonsPicker";
import SaleInvoiceModal, { formatPrintLog } from "@/components/admin/SaleInvoiceModal";
import ServiceNoteField from "@/components/admin/ServiceNoteField";
import {
    Plus,
    Pencil,
    Trash2,
    Check,
    X,
    Search,
    FileText,
    Upload,
    ShoppingCart,
    Receipt,
    Percent,
    Download,
    Printer,
} from "lucide-react";

// ---------- Sales Tab ----------
function SaleEditModal({ sale, products, onClose, onSaved }) {
    const { content } = useSiteContent();
    const lekRate = content.currency_lek_per_eur || 100;
    const toLek = (eur) => `${Math.round((parseFloat(eur) || 0) * lekRate).toLocaleString()} Lek`;
    const [items, setItems] = useState(
        sale.items.map((it) => {
            const prod = products.find((p) => p.id === it.product_id);
            return {
                ...it,
                stock_qty: (prod?.stock_qty ?? 0) + it.qty, // available now = current stock + already-allocated qty
            };
        })
    );
    const [discount, setDiscount] = useState(sale.discount_percent || 0);
    const [note, setNote] = useState(sale.note || "");
    const [reasons, setReasons] = useState(sale.failure_reasons || []);
    const [serviceNote, setServiceNote] = useState(sale.service_note || "");
    const [services, setServices] = useState(sale.services || []);
    const [serviceLines, setServiceLines] = useState((sale.service_lines || []).map((l) => ({ name: l.name, qty: l.qty || 1, price_lek: l.price_lek })));
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    const subtotal = items.reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseInt(it.qty, 10) || 0), 0);
    const discountAmount = subtotal * (parseFloat(discount) || 0) / 100;
    const servicesTotal = servicesTotalEur(serviceLines, content);
    const total = subtotal - discountAmount + servicesTotal;

    const update = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

    const save = async () => {
        setErr("");
        for (const it of items) {
            if (!it.qty || it.qty < 1) return setErr(`Sasi e pavlefshme për ${it.part_number}.`);
            if (it.qty > it.stock_qty) return setErr(`Stok i pamjaftueshëm për ${it.part_number} (ke ${it.stock_qty}).`);
        }
        setSaving(true);
        try {
            await api.patch(`/admin/sales/${sale.id}`, {
                items: items.map((it) => ({
                    product_id: it.product_id,
                    qty: parseInt(it.qty, 10),
                    unit_price: parseFloat(it.unit_price) || 0,
                })),
                discount_percent: parseFloat(discount) || 0,
                note: note || null,
                failure_reasons: reasons,
                service_note: serviceNote,
                services,
                service_lines: serviceLinesPayload(serviceLines),
            });
            onSaved();
        } catch (e) {
            setErr(e.response?.data?.detail || "Edit failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="sale-edit-modal">
                <div className="px-4 py-3 border-b border-slate-200 flex justify-between">
                    <div>
                        <div className="font-display font-bold uppercase">Edit sale #{sale.id.slice(0, 8)}</div>
                        <div className="font-mono text-[11px] text-slate-500">{sale.customer_name} · {new Date(sale.created_at).toLocaleDateString()}</div>
                    </div>
                    <button onClick={onClose}><X className="w-4 h-4" /></button>
                </div>
                <div className="p-4 space-y-3">
                    <table className="w-full text-sm border border-slate-200">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-2 py-2 text-left label-kicker">Part</th>
                                <th className="px-2 py-2 label-kicker w-20">Qty</th>
                                <th className="px-2 py-2 label-kicker w-28">Unit €</th>
                                <th className="px-2 py-2 label-kicker w-24 text-right">Line</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it, idx) => (
                                <tr key={idx} className="border-b border-slate-100">
                                    <td className="px-2 py-2">
                                        <div className="font-bold text-xs">{it.part_number}</div>
                                        <div className="font-mono text-[10px] text-slate-500">stock available: {it.stock_qty}</div>
                                    </td>
                                    <td className="px-2 py-2">
                                        <input
                                            type="number"
                                            min="1"
                                            max={it.stock_qty}
                                            value={it.qty}
                                            onChange={(e) => update(idx, { qty: e.target.value })}
                                            className="input-field !py-1 !px-2 !text-xs"
                                            data-testid={`edit-sale-item-qty-${idx}`}
                                        />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={it.unit_price}
                                            onChange={(e) => update(idx, { unit_price: e.target.value })}
                                            className="input-field !py-1 !px-2 !text-xs"
                                            data-testid={`edit-sale-item-price-${idx}`}
                                        />
                                    </td>
                                    <td className="px-2 py-2 text-right font-display font-bold">
                                        <div>€{((parseFloat(it.unit_price) || 0) * (parseInt(it.qty, 10) || 0)).toFixed(2)}</div>
                                        <div className="text-[10px] font-mono text-slate-500 font-normal">{toLek((parseFloat(it.unit_price) || 0) * (parseInt(it.qty, 10) || 0))}</div>
                                    </td>
                                    <td className="px-2 py-2">
                                        <button onClick={() => remove(idx)} className="text-red-600" disabled={items.length <= 1}>
                                            <X className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-kicker mb-1 block">Discount %</label>
                            <input type="number" min="0" max="100" step="0.5" value={discount} onChange={(e) => setDiscount(e.target.value)} className="input-field !py-1" data-testid="edit-sale-discount" />
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">Note</label>
                            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className="input-field !py-1" data-testid="edit-sale-note" />
                        </div>
                    </div>

                    <ServiceLinesEditor lines={serviceLines} onChange={setServiceLines} testIdPrefix="edit-sale-svc" />
                    <FailureReasonsPicker value={reasons} onChange={setReasons} testIdPrefix="edit-sale-reason" />
                    <FailureReasonsPicker kind="services" value={services} onChange={setServices} testIdPrefix="edit-sale-service" />
                    <ServiceNoteField value={serviceNote} onChange={setServiceNote} testId="edit-sale-service-note" />

                    <div className="bg-slate-50 border border-slate-200 p-3 flex justify-between items-center">
                        <div className="font-display font-bold uppercase">Total</div>
                        <div className="text-right">
                            <div className="font-display font-black text-xl" data-testid="edit-sale-total">€{total.toFixed(2)}</div>
                            <div className="text-[11px] font-mono text-slate-600">{toLek(total)}</div>
                        </div>
                    </div>

                    {err && <div className="text-xs text-red-600 font-mono bg-red-50 border border-red-200 p-2">{err}</div>}
                </div>
                <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={save} disabled={saving} className="btn-primary" data-testid="edit-sale-save">
                        <Check className="w-4 h-4" /> {saving ? "Saving..." : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function SalesTab() {
    const { content } = useSiteContent();
    const lekRate = content.currency_lek_per_eur || 100;
    const toLek = (eur) => `${Math.round((parseFloat(eur) || 0) * lekRate).toLocaleString()} Lek`;
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [sales, setSales] = useState([]);
    const [view, setView] = useState("new"); // 'new' | 'history'
    const [customerId, setCustomerId] = useState("");
    const [items, setItems] = useState([]); // {product_id, name, part_number, qty, unit_price, stock_qty}
    const [productSearch, setProductSearch] = useState("");
    const [productHits, setProductHits] = useState([]);
    const [searching, setSearching] = useState(false);
    const [discount, setDiscount] = useState(0);
    const [note, setNote] = useState("");
    const [saleReasons, setSaleReasons] = useState([]);
    const [saleServiceNote, setSaleServiceNote] = useState("");
    const [saleServices, setSaleServices] = useState([]);
    const [saleServiceLines, setSaleServiceLines] = useState([]);
    const [saleCurrency, setSaleCurrency] = useState("LEK"); // EUR | LEK — picked at sale creation
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null);
    const [confirm, setConfirm] = useState(null);
    // History filters + edit + invoice viewer
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [editingSale, setEditingSale] = useState(null);
    const [receiptFor, setReceiptFor] = useState(null);
    // After a sale is created, optionally open the Label Designer pre-filled with the
    // newly sold products so the user can print labels in one click.
    const [labelProductIds, setLabelProductIds] = useState([]);
    const [invoiceSale, setInvoiceSale] = useState(null);

    const loadCustomers = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/customers");
            setCustomers(data);
        } catch {}
    }, []);

    const loadProducts = useCallback(async () => {
        try {
            const { data } = await api.get("/products", { params: { per_page: 100 } });
            setProducts(data);
        } catch {}
    }, []);

    // Server-side debounced search for the "Add product" picker so that
    // ANY product (including newly added / beyond the first 100) is findable.
    useEffect(() => {
        const s = productSearch.trim();
        if (!s) { setProductHits([]); return; }
        setSearching(true);
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.get("/products", { params: { q: s, per_page: 20, page: 1 } });
                setProductHits(data || []);
            } catch {
                setProductHits([]);
            } finally {
                setSearching(false);
            }
        }, 200);
        return () => clearTimeout(handle);
    }, [productSearch]);

    const loadSales = useCallback(async () => {
        try {
            const params = { limit: 500 };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const { data } = await api.get("/admin/sales", { params });
            setSales(data);
        } catch {}
    }, [dateFrom, dateTo]);

    useEffect(() => {
        loadCustomers();
        loadProducts();
        loadSales();
    }, [loadCustomers, loadProducts, loadSales]);

    // Client-side quick search across customer name/phone/email/NIPT and item part numbers
    const filteredSales = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return sales;
        return sales.filter((s) => {
            if ((s.customer_name || "").toLowerCase().includes(q)) return true;
            if ((s.customer_phone || "").toLowerCase().includes(q)) return true;
            if ((s.customer_email || "").toLowerCase().includes(q)) return true;
            if ((s.customer_nipt || "").toLowerCase().includes(q)) return true;
            if ((s.note || "").toLowerCase().includes(q)) return true;
            if ((s.items || []).some((it) => (it.part_number || "").toLowerCase().includes(q) || (it.name || "").toLowerCase().includes(q))) return true;
            return false;
        });
    }, [sales, searchQuery]);

    const addItem = (p) => {
        if (items.some((i) => i.product_id === p.id)) return;
        setItems([...items, {
            product_id: p.id,
            name: p.name,
            part_number: p.part_number || p.name,
            qty: 1,
            unit_price: p.price || 0,
            stock_qty: p.stock_qty ?? 0,
        }]);
        setProductSearch("");
    };

    const updateItem = (idx, patch) => {
        setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };

    const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

    const subtotal = items.reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseInt(it.qty, 10) || 0), 0);
    const discountAmount = subtotal * (parseFloat(discount) || 0) / 100;
    const servicesTotal = servicesTotalEur(saleServiceLines, content);
    const total = subtotal - discountAmount + servicesTotal;

    const save = async () => {
        setError("");
        setSuccess(null);
        if (!customerId) return setError("Zgjidhni klientin.");
        if (items.length === 0 && saleServiceLines.length === 0) return setError("Shto të paktën një produkt ose shërbim.");
        for (const it of items) {
            if (!it.qty || it.qty < 1) return setError(`Sasi e pavlefshme për ${it.part_number}.`);
            if (it.qty > it.stock_qty) return setError(`Stok i pamjaftueshëm për ${it.part_number} (ke ${it.stock_qty}).`);
        }
        setSaving(true);
        try {
            const payload = {
                customer_id: customerId,
                items: items.map((it) => ({
                    product_id: it.product_id,
                    qty: parseInt(it.qty, 10),
                    unit_price: parseFloat(it.unit_price) || 0,
                })),
                discount_percent: parseFloat(discount) || 0,
                note: note || null,
                display_currency: saleCurrency,
                failure_reasons: saleReasons,
                service_note: saleServiceNote || null,
                services: saleServices,
                service_lines: serviceLinesPayload(saleServiceLines),
            };
            const { data } = await api.post("/admin/sales", payload);
            setSuccess(data);
            setItems([]);
            setDiscount(0);
            setNote("");
            setSaleReasons([]);
            setSaleServiceNote("");
            setSaleServices([]);
            setSaleServiceLines([]);
            setCustomerId("");
            setSaleCurrency("LEK");
            loadProducts();
            loadSales();
        } catch (e) {
            setError(e.response?.data?.detail || "Shitja dështoi.");
        } finally {
            setSaving(false);
        }
    };

    const askDeleteSale = (sale) => {
        setConfirm({
            title: "Fshij shitjen?",
            message: `Fshirja e shitjes së ${sale.customer_name || sale.customer_email} (${sale.display_currency === "EUR" ? `€${sale.total.toFixed(2)}` : toLek(sale.total)}) do të rikthejë stokun.`,
            onConfirm: async () => {
                await api.delete(`/admin/sales/${sale.id}`);
                setConfirm(null);
                loadProducts();
                loadSales();
            },
        });
    };

    const filteredProducts = productSearch.trim() ? productHits.slice(0, 12) : [];

    return (
        <div data-testid="admin-sales-tab" className="space-y-6">
            {/* View switcher */}
            <div className="flex gap-2 border-b border-slate-200">
                <button
                    onClick={() => setView("new")}
                    className={`px-4 py-2 font-display font-bold uppercase text-xs tracking-wider border-b-2 -mb-px transition-colors ${view === "new" ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-transparent text-slate-600"}`}
                    data-testid="sales-view-new"
                >
                    <Plus className="w-3.5 h-3.5 inline -mt-0.5" /> New sale
                </button>
                <button
                    onClick={() => setView("history")}
                    className={`px-4 py-2 font-display font-bold uppercase text-xs tracking-wider border-b-2 -mb-px transition-colors ${view === "history" ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-transparent text-slate-600"}`}
                    data-testid="sales-view-history"
                >
                    <Receipt className="w-3.5 h-3.5 inline -mt-0.5" /> History ({sales.length})
                </button>
            </div>

            {view === "new" && (
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left: customer + product picker */}
                    <div className="lg:col-span-2 space-y-4">
                        <div>
                            <label className="label-kicker mb-1 block">Customer *</label>
                            <CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} />
                            {customers.length === 0 && (
                                <p className="text-xs text-slate-500 mt-1 font-mono" data-testid="sales-no-customers-hint">
                                    Shto klientë te tab-i <span className="font-bold">Customers</span> para se të bësh shitje.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="label-kicker mb-1 block">Add product</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    placeholder="Search by name or part number..."
                                    className="input-field"
                                    data-testid="sales-product-search"
                                />
                                {filteredProducts.length > 0 && (
                                    <div className="absolute z-10 top-full left-0 right-0 border border-slate-300 bg-white mt-1 max-h-64 overflow-y-auto" data-testid="sales-product-suggestions">
                                        {filteredProducts.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => addItem(p)}
                                                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between text-sm border-b border-slate-100"
                                                data-testid={`sales-product-option-${p.id}`}
                                            >
                                                <div>
                                                    <div className="font-bold">{p.part_number || p.name}</div>
                                                    <div className="font-mono text-[11px] text-slate-500">{p.category_name}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-display font-bold">€{(p.price || 0).toFixed(2)}</div>
                                                    <div className="font-mono text-[10px] text-slate-500">stock: {p.stock_qty ?? 0}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Items table */}
                        <div className="border border-slate-200">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="px-2 py-2 text-left label-kicker">Part</th>
                                        <th className="px-2 py-2 label-kicker w-20">Qty</th>
                                        <th className="px-2 py-2 label-kicker w-28">Unit €</th>
                                        <th className="px-2 py-2 label-kicker w-24 text-right">Line</th>
                                        <th className="w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 && (
                                        <tr><td colSpan="5" className="px-3 py-6 text-center text-slate-500 font-mono text-xs">Nuk ka produkte. Kërko më sipër për të shtuar.</td></tr>
                                    )}
                                    {items.map((it, idx) => (
                                        <tr key={idx} className="border-b border-slate-100" data-testid={`sales-item-row-${idx}`}>
                                            <td className="px-2 py-2">
                                                <div className="font-bold text-xs">{it.part_number}</div>
                                                <div className="font-mono text-[10px] text-slate-500">stock: {it.stock_qty}</div>
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={it.stock_qty}
                                                    value={it.qty}
                                                    onChange={(e) => updateItem(idx, { qty: e.target.value })}
                                                    className="input-field !py-1 !px-2 !text-xs"
                                                    data-testid={`sales-item-qty-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={it.unit_price}
                                                    onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                                                    className="input-field !py-1 !px-2 !text-xs"
                                                    data-testid={`sales-item-price-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2 text-right font-display font-bold">
                                                <div>€{((parseFloat(it.unit_price) || 0) * (parseInt(it.qty, 10) || 0)).toFixed(2)}</div>
                                                <div className="text-[10px] font-mono text-slate-500 font-normal">{toLek((parseFloat(it.unit_price) || 0) * (parseInt(it.qty, 10) || 0))}</div>
                                            </td>
                                            <td className="px-2 py-2">
                                                <button onClick={() => removeItem(idx)} className="text-red-600" data-testid={`sales-item-remove-${idx}`}>
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <ServiceLinesEditor lines={saleServiceLines} onChange={setSaleServiceLines} testIdPrefix="sales-svc" />

                        <div>
                            <label className="label-kicker mb-1 block">Note (opsionale)</label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows="2"
                                className="input-field"
                                placeholder="p.sh. pagesa cash, etj."
                                data-testid="sales-note-input"
                            />
                        </div>

                        <FailureReasonsPicker value={saleReasons} onChange={setSaleReasons} testIdPrefix="sales-reason" />
                        <FailureReasonsPicker kind="services" value={saleServices} onChange={setSaleServices} testIdPrefix="sales-service" />
                        <ServiceNoteField value={saleServiceNote} onChange={setSaleServiceNote} testId="sales-service-note" />
                    </div>

                    {/* Right: summary */}
                    <div className="lg:col-span-1">
                        <div className="border border-slate-200 bg-slate-50 p-4 sticky top-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="font-display font-black uppercase text-lg">Summary</div>
                                {/* Currency picker — saved with the sale, drives invoice & PDF */}
                                <div className="inline-flex border-2 border-slate-900 overflow-hidden" data-testid="sales-currency-toggle">
                                    <button
                                        type="button"
                                        onClick={() => setSaleCurrency("EUR")}
                                        className={`px-2.5 py-1 text-[10px] font-display font-black uppercase tracking-wider transition-colors ${saleCurrency === "EUR" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                                        data-testid="sales-currency-eur"
                                    >
                                        EUR
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSaleCurrency("LEK")}
                                        className={`px-2.5 py-1 text-[10px] font-display font-black uppercase tracking-wider border-l-2 border-slate-900 transition-colors ${saleCurrency === "LEK" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                                        data-testid="sales-currency-lek"
                                    >
                                        LEK
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2 text-sm font-mono">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Subtotal</span>
                                    <div className="text-right" data-testid="sales-subtotal">
                                        {saleCurrency === "LEK" ? toLek(subtotal) : `€${subtotal.toFixed(2)}`}
                                    </div>
                                </div>

                                <div className="py-2 border-y border-slate-200">
                                    <label className="flex items-center gap-2 label-kicker text-slate-500">
                                        <Percent className="w-3 h-3" /> Discount %
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        value={discount}
                                        onChange={(e) => setDiscount(e.target.value)}
                                        className="input-field !py-1 !px-2 !text-sm mt-1"
                                        data-testid="sales-discount-input"
                                    />
                                    <div className="flex justify-between mt-2 text-red-600">
                                        <span>- discount</span>
                                        <div className="text-right" data-testid="sales-discount-amount">
                                            {saleCurrency === "LEK" ? `-${toLek(discountAmount)}` : `-€${discountAmount.toFixed(2)}`}
                                        </div>
                                    </div>
                                </div>

                                {servicesTotal > 0 && (
                                    <div className="flex justify-between pt-2 text-slate-700">
                                        <span>+ shërbimet</span>
                                        <div className="text-right" data-testid="sales-services-total">
                                            {saleCurrency === "LEK" ? toLek(servicesTotal) : `€${servicesTotal.toFixed(2)}`}
                                        </div>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2">
                                    <span className="font-display font-bold uppercase">Total</span>
                                    <div className="font-display font-black text-lg" data-testid="sales-total">
                                        {saleCurrency === "LEK" ? toLek(total) : `€${total.toFixed(2)}`}
                                    </div>
                                </div>
                            </div>

                            {error && <div className="mt-3 bg-red-50 border border-red-200 p-2 text-xs text-red-700 font-mono" data-testid="sales-error">{error}</div>}
                            {success && (
                                <div className="mt-3 bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-700 font-mono flex items-center justify-between gap-2 flex-wrap" data-testid="sales-success">
                                    <span>✓ Shitje #{success.id.slice(0, 8)} — €{success.total.toFixed(2)}</span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setReceiptFor(success)}
                                            className="btn-outline !py-1 !text-[10px]"
                                            data-testid="sales-success-print-receipt"
                                        >
                                            <Printer className="w-3 h-3" /> Faturë 80mm
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const ids = (success.items || [])
                                                    .map((it) => it.product_id)
                                                    .filter(Boolean);
                                                if (ids.length === 0) {
                                                    alert("Nuk ka produkte për etiketim.");
                                                    return;
                                                }
                                                setLabelProductIds(ids);
                                            }}
                                            className="btn-primary !py-1 !text-[10px]"
                                            data-testid="sales-success-print-labels"
                                            title="Hap Label Designer me produktet e kësaj shitjeje të para-zgjedhura"
                                        >
                                            <FileText className="w-3 h-3" /> Etiketa
                                        </button>
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={save}
                                disabled={saving || !customerId || (items.length === 0 && saleServiceLines.length === 0)}
                                className="btn-primary w-full mt-3 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="sales-save-button"
                            >
                                {saving ? "Ruajtja…" : <><ShoppingCart className="w-4 h-4" /> Confirm sale</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {view === "history" && (
                <div className="space-y-3">
                    {/* Date filter bar */}
                    <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
                        <div>
                            <label className="label-kicker mb-1 block">From</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="input-field !py-1 !text-xs"
                                data-testid="sales-date-from"
                            />
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">To</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="input-field !py-1 !text-xs"
                                data-testid="sales-date-to"
                            />
                        </div>
                        <button onClick={() => loadSales()} className="btn-primary !py-1.5 !text-xs" data-testid="sales-date-apply">
                            <Search className="w-3.5 h-3.5" /> Apply
                        </button>
                        {(dateFrom || dateTo) && (
                            <button
                                onClick={() => { setDateFrom(""); setDateTo(""); setTimeout(loadSales, 0); }}
                                className="text-xs font-mono text-slate-500 underline"
                                data-testid="sales-date-reset"
                            >
                                Reset
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                const params = {};
                                if (dateFrom) params.date_from = dateFrom;
                                if (dateTo) params.date_to = dateTo;
                                downloadAuthed("/admin/sales/export", "turbo-cimi-sales.xlsx", params)
                                    .catch((e) => alert(e.message));
                            }}
                            className="btn-outline !py-1.5 !text-xs"
                            data-testid="sales-export-xlsx"
                            title="Export filtered sales to Excel (sale + items detail)"
                        >
                            <Upload className="w-3.5 h-3.5 rotate-180" /> Export shitjet
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const params = {};
                                if (dateFrom) params.date_from = dateFrom;
                                if (dateTo) params.date_to = dateTo;
                                const fname = `turbo-cimi-produktet-shitur${dateFrom ? `_${dateFrom}` : ""}${dateTo ? `_${dateTo}` : ""}.xlsx`;
                                downloadAuthed("/admin/sales/products-aggregate/export", fname, params)
                                    .catch((e) => alert(e.message));
                            }}
                            className="btn-primary !py-1.5 !text-xs"
                            data-testid="sales-export-products-xlsx"
                            title="Export agregat sipas produktit (Part №, Name, Category, Qty, Total) për porositë e radhës"
                        >
                            <Upload className="w-3.5 h-3.5 rotate-180" /> Export produktet
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const params = {};
                                if (dateFrom) params.date_from = dateFrom;
                                if (dateTo) params.date_to = dateTo;
                                if (searchQuery.trim()) params.search = searchQuery.trim();
                                const fname = `turbo-cimi-shitjet-detajuar${dateFrom ? `_${dateFrom}` : ""}${dateTo ? `_${dateTo}` : ""}.xlsx`;
                                downloadAuthed("/admin/sales/summary/export", fname, params)
                                    .catch((e) => alert(e.message));
                            }}
                            className="btn-outline !py-1.5 !text-xs"
                            data-testid="sales-export-summary-xlsx"
                            title="Export Date, Customer, Items, Discount, Total, Note (një rresht për shitje)"
                        >
                            <Upload className="w-3.5 h-3.5 rotate-180" /> Export përmbledhje
                        </button>
                        <div className="ml-auto font-mono text-xs text-slate-600 text-right" data-testid="sales-history-count">
                            <div>{filteredSales.length} shitje · Total €{filteredSales.reduce((s, x) => s + (x.total || 0), 0).toFixed(2)}</div>
                            <div className="text-[10px] text-slate-500">{toLek(filteredSales.reduce((s, x) => s + (x.total || 0), 0))}</div>
                        </div>
                    </div>

                    {/* Quick search */}
                    <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Kërko emrin e klientit, telefon, NIPT, ose part №..."
                            className="input-field pl-10"
                            data-testid="sales-search-input"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                                data-testid="sales-search-clear"
                                title="Pastro"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="border border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-left">
                                <th className="px-3 py-2 label-kicker">Date</th>
                                <th className="px-3 py-2 label-kicker">Customer</th>
                                <th className="px-3 py-2 label-kicker">Items</th>
                                <th className="px-3 py-2 label-kicker">Discount</th>
                                <th className="px-3 py-2 label-kicker text-right">Total</th>
                                <th className="w-44"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSales.length === 0 && (
                                <tr><td colSpan="6" className="px-3 py-8 text-center font-mono text-slate-500">
                                    {searchQuery ? `Asnjë shitje nuk përputhet me "${searchQuery}".` : "Asnjë shitje akoma."}
                                </td></tr>
                            )}
                            {filteredSales.map((s) => (
                                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sale-row-${s.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{new Date(s.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2">
                                        <div className="font-bold text-xs">{s.customer_name}</div>
                                        <div className="font-mono text-[11px] text-slate-500">{s.customer_phone || s.customer_email || ""}</div>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {s.items.map((it) => `${it.part_number} × ${it.qty}`).join(", ")}
                                        {s.last_print && (
                                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5" data-testid={`sale-last-print-${s.id}`} title={`${(s.print_history || []).length} printime`}>
                                                <Printer className="w-3 h-3" /> {formatPrintLog(s.last_print)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs text-red-600">
                                        {s.discount_percent > 0 ? `${s.discount_percent}% (−${s.display_currency === "EUR" ? `€${s.discount_amount.toFixed(2)}` : toLek(s.discount_amount)})` : "—"}
                                    </td>
                                    <td className="px-3 py-2 font-display font-black text-right">
                                        {s.display_currency === "EUR"
                                            ? `€${s.total.toFixed(2)}`
                                            : toLek(s.total)}
                                        <div className="text-[9px] font-mono text-slate-400 uppercase tracking-wider">
                                            {s.display_currency || "LEK"}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button onClick={() => setInvoiceSale(s)} className="p-1.5 text-slate-500 hover:text-[hsl(var(--primary))]" title="View invoice" data-testid={`view-invoice-${s.id}`}>
                                            <Receipt className="w-4 h-4" />
                                        </button>
                                        <WaReadyButton sale={s} onDone={loadSales} />
                                        <WaSaleButton sale={s} />
                                        <button onClick={() => setReceiptFor(s)} className="p-1.5 text-slate-500 hover:text-[hsl(var(--primary))]" title="Print 80mm receipt" data-testid={`print-receipt-${s.id}`}>
                                            <Printer className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setEditingSale(s)} className="p-1.5 text-slate-500 hover:text-[hsl(var(--primary))]" title="Edit sale" data-testid={`edit-sale-${s.id}`}>
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => askDeleteSale(s)} className="p-1.5 text-slate-500 hover:text-red-600" title="Fshij & rikthe stokun" data-testid={`delete-sale-${s.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {editingSale && (
                <SaleEditModal
                    sale={editingSale}
                    products={products}
                    onClose={() => setEditingSale(null)}
                    onSaved={() => { setEditingSale(null); loadSales(); loadProducts(); }}
                />
            )}

            {invoiceSale && (
                <SaleInvoiceModal
                    sale={invoiceSale}
                    onClose={() => setInvoiceSale(null)}
                    onEdit={(s) => { setInvoiceSale(null); setEditingSale(s); }}
                    onChanged={loadSales}
                />
            )}

            <ReceiptPrinter
                open={!!receiptFor}
                sale={receiptFor}
                onClose={() => setReceiptFor(null)}
            />

            <LabelEditor
                open={labelProductIds.length > 0}
                onClose={() => setLabelProductIds([])}
                initialProductIds={labelProductIds}
            />

            {confirm && (
                <ConfirmDialog
                    title={confirm.title}
                    message={confirm.message}
                    onConfirm={confirm.onConfirm}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </div>
    );
}

